/**
 * Phase 138H — Public One-Click Student Fee Payment Page (Magic Link)
 *
 * Route: /pay/student-fee/:feeId?token=<jwt>&ch=email|whatsapp
 *
 * Lets a parent pay a specific overdue academy fee WITHOUT logging in.
 * The token is minted + dispatched by services/student_fee_dunning.py
 * during the daily 09:30 IST runner (pre-due / due-day / +2d / +4d / +6d).
 *
 * Flow:
 *   1. Mount → GET /api/pay/student-fee/{id}/context → renders fee card
 *   2. Tap "Pay Now" → POST /api/pay/student-fee/{id}/create-order →
 *      opens Razorpay checkout (using ACADEMY's own keys)
 *   3. Razorpay handler → POST /api/pay/student-fee/{id}/verify →
 *      marks fee paid + fires receipt email + WhatsApp
 *   4. Success screen with confirmation
 *
 * Public — bare layout, no auth headers, no app shell.
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  ShieldCheck, Loader2, CreditCard, CheckCircle2, AlertTriangle,
  Lock, Clock, User,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';

function loadRazorpay() {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve(false);
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const s = document.createElement('script');
    s.src = RAZORPAY_SCRIPT;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function rupees(amount) {
  if (amount === null || amount === undefined) return '—';
  return `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

async function fetchPublic(path, options = {}) {
  const r = await fetch(`${BACKEND_URL}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(data?.detail || `HTTP ${r.status}`);
    err.status = r.status;
    err.data = data;
    throw err;
  }
  return data;
}

function EmptyState({ icon: Icon, title, body, ctaLabel, ctaTo }) {
  return (
    <div className="text-center py-10" data-testid="public-pay-fee-empty-state">
      <div className="mx-auto w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
        <Icon className="h-7 w-7 text-slate-400" />
      </div>
      <h2 className="text-lg font-black text-slate-900 mb-1">{title}</h2>
      <p className="text-sm text-slate-500 max-w-xs mx-auto leading-relaxed">{body}</p>
      {ctaTo && (
        <Link to={ctaTo} className="inline-flex items-center justify-center gap-1.5 mt-5 px-4 py-2 text-xs font-bold text-white bg-slate-900 rounded-lg">
          {ctaLabel}
        </Link>
      )}
    </div>
  );
}

export default function PublicPayStudentFee() {
  const { feeId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [contextStatus, setContextStatus] = useState(null);
  const [contextReason, setContextReason] = useState('');
  const [paying, setPaying] = useState(false);
  const [stage, setStage] = useState(''); // 'creating' | 'opening' | 'verifying'
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState(null);

  const loadContext = useCallback(async () => {
    if (!token) {
      setContextStatus('invalid');
      setContextReason('Missing token. Open the link directly from your email or WhatsApp.');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchPublic(
        `/api/pay/student-fee/${encodeURIComponent(feeId)}/context?token=${encodeURIComponent(token)}`,
      );
      setContextStatus(data.status);
      setContextReason(data.reason || '');
      if (data.status === 'valid') setCtx(data);
      if (data.status === 'already_paid') setPaid(true);
    } catch (e) {
      setContextStatus('error');
      setContextReason(e?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [feeId, token]);

  useEffect(() => { loadContext(); }, [loadContext]);

  const startCheckout = useCallback(async () => {
    if (paying || !ctx) return;
    setPaying(true);
    setError(null);
    setStage('creating');
    try {
      const orderRes = await fetchPublic(
        `/api/pay/student-fee/${encodeURIComponent(feeId)}/create-order?token=${encodeURIComponent(token)}`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      const { order, key_id, mocked, fallback_reason } = orderRes;
      if (!order || !order.id) throw new Error('Order creation failed');

      if (mocked) {
        // Academy hasn't configured Razorpay — surface the reason and stop.
        setError(fallback_reason || 'Online payment is not available right now. Please contact the academy.');
        setPaying(false);
        setStage('');
        return;
      }

      setStage('opening');
      const ok = await loadRazorpay();
      if (!ok || !window.Razorpay) {
        throw new Error('Razorpay script failed to load.');
      }
      const options = {
        key: key_id,
        amount: order.amount,
        currency: order.currency || 'INR',
        order_id: order.id,
        name: ctx.academy?.name || 'Academy',
        description: `Fee Payment — ${ctx.student?.name || 'Student'}`,
        prefill: {},
        theme: { color: '#16a34a' },
        modal: { ondismiss: () => { setPaying(false); setStage(''); } },
        handler: async (resp) => {
          try {
            setStage('verifying');
            await fetchPublic(
              `/api/pay/student-fee/${encodeURIComponent(feeId)}/verify?token=${encodeURIComponent(token)}`,
              {
                method: 'POST',
                body: JSON.stringify({
                  razorpay_order_id: resp.razorpay_order_id,
                  razorpay_payment_id: resp.razorpay_payment_id,
                  razorpay_signature: resp.razorpay_signature,
                }),
              },
            );
            setPaid(true);
            setPaying(false);
            setStage('');
          } catch (e) {
            setError(e?.data?.detail || e.message || 'Payment verification failed');
            setPaying(false);
            setStage('');
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (r) => {
        setError(r?.error?.description || 'Payment failed');
        setPaying(false);
        setStage('');
      });
      rzp.open();
    } catch (e) {
      setError(e?.data?.detail || e.message || 'Checkout failed');
      setPaying(false);
      setStage('');
    }
  }, [paying, ctx, feeId, token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center p-4" data-testid="public-pay-fee-page">
      <div className="w-full max-w-md">
        {/* Brand strip */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-black tracking-[0.2em] uppercase text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5" /> Secure · powered by Out-Play.in
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
          {loading && (
            <div className="p-10 text-center" data-testid="public-pay-fee-loading">
              <Loader2 className="h-7 w-7 mx-auto animate-spin text-slate-400" />
              <p className="mt-3 text-sm text-slate-500">Verifying payment link…</p>
            </div>
          )}

          {!loading && paid && (
            <div className="p-8 text-center" data-testid="public-pay-fee-success">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-9 w-9 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-900 mb-2">Payment received</h2>
              <p className="text-sm text-slate-600 max-w-xs mx-auto leading-relaxed">
                Thank you! A receipt has been mailed to your registered email.
                The academy has been notified.
              </p>
              <p className="text-[10px] text-slate-400 mt-5">
                You can close this tab.
              </p>
            </div>
          )}

          {!loading && !paid && contextStatus === 'valid' && ctx && (
            <div className="p-7" data-testid="public-pay-fee-card">
              <div className="text-[10px] font-black tracking-[0.2em] uppercase text-emerald-700 mb-1">
                💳 Academy Fee · One-Click Pay
              </div>
              <h1 className="text-2xl font-black text-slate-900 mb-1">
                Pay your fee
              </h1>
              <p className="text-xs text-slate-500 mb-5">
                {ctx.academy?.name || 'Your academy'} · No login required
              </p>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-5">
                <div className="flex items-start gap-2 mb-3">
                  <User className="h-4 w-4 text-emerald-700 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-[10px] font-black tracking-[0.15em] uppercase text-emerald-900">Student</div>
                    <div className="text-sm font-bold text-slate-800 mt-0.5" data-testid="public-pay-fee-student-name">
                      {ctx.student?.name || '—'}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pb-3 border-b border-emerald-200">
                  <div>
                    <div className="text-[10px] font-black tracking-[0.15em] uppercase text-emerald-900">Fee Type</div>
                    <div className="text-xs font-bold text-slate-800 mt-0.5 capitalize">
                      {(ctx.fee.fee_type || 'fee')}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-black tracking-[0.15em] uppercase text-emerald-900">Due Date</div>
                    <div className="text-xs font-bold text-slate-800 mt-0.5" data-testid="public-pay-fee-due-date">
                      {(ctx.fee.due_date || '').slice(0, 10) || '—'}
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="text-[10px] font-black tracking-[0.15em] uppercase text-emerald-900">Amount Due</div>
                  <div className="text-4xl font-black text-slate-900 tabular-nums mt-1" data-testid="public-pay-fee-amount">
                    {rupees(ctx.fee.amount)}
                  </div>
                  {ctx.fee.period && (
                    <div className="text-[10px] text-slate-500 mt-1">
                      Period: {ctx.fee.period}
                    </div>
                  )}
                </div>
              </div>

              {ctx.mocked && (
                <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex gap-2" data-testid="public-pay-fee-no-rzp">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <div>
                    Online payment is not available right now. Please contact <b>{ctx.academy?.name}</b> to pay offline.
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800 flex gap-2" data-testid="public-pay-fee-error">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
                </div>
              )}

              <button
                onClick={startCheckout}
                disabled={paying || ctx.mocked}
                data-testid="public-pay-fee-cta"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 text-base font-black text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {paying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {stage === 'verifying' ? 'Confirming payment…' : stage === 'opening' ? 'Opening Razorpay…' : 'Preparing…'}
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4" /> Pay {rupees(ctx.fee.amount)} Now
                  </>
                )}
              </button>

              <div className="mt-5 grid grid-cols-3 gap-2 text-[10px] text-slate-500">
                <div className="text-center">
                  <Lock className="h-3.5 w-3.5 mx-auto mb-1 text-emerald-600" />
                  <div>Secure Razorpay</div>
                </div>
                <div className="text-center">
                  <ShieldCheck className="h-3.5 w-3.5 mx-auto mb-1 text-emerald-600" />
                  <div>No login</div>
                </div>
                <div className="text-center">
                  <Clock className="h-3.5 w-3.5 mx-auto mb-1 text-emerald-600" />
                  <div>Instant receipt</div>
                </div>
              </div>
            </div>
          )}

          {!loading && !paid && contextStatus === 'expired' && (
            <EmptyState
              icon={Clock}
              title="Payment link expired"
              body="This link is older than 72 hours. Please contact your academy for a fresh link."
              ctaLabel="Open Out-Play"
              ctaTo="/"
            />
          )}

          {!loading && !paid && contextStatus === 'invalid' && (
            <EmptyState
              icon={AlertTriangle}
              title="Payment link invalid"
              body={contextReason || "This payment link couldn't be verified. Please contact your academy."}
              ctaLabel="Open Out-Play"
              ctaTo="/"
            />
          )}

          {!loading && !paid && contextStatus === 'fee_not_found' && (
            <EmptyState
              icon={AlertTriangle}
              title="Fee not found"
              body="We couldn't locate this fee. It may have been deleted by your academy."
              ctaLabel="Open Out-Play"
              ctaTo="/"
            />
          )}

          {!loading && !paid && contextStatus === 'scope_mismatch' && (
            <EmptyState
              icon={AlertTriangle}
              title="Token does not match"
              body="This link points to a different fee. Please use the latest reminder from your email or WhatsApp."
              ctaLabel="Open Out-Play"
              ctaTo="/"
            />
          )}

          {!loading && !paid && contextStatus === 'error' && (
            <EmptyState
              icon={AlertTriangle}
              title="Couldn't load fee"
              body={contextReason || 'Something went wrong. Please try again later.'}
              ctaLabel="Open Out-Play"
              ctaTo="/"
            />
          )}
        </div>

        <p className="text-center text-[10px] text-slate-400 mt-4">
          Trouble paying? Reply to the email or WhatsApp message you received.
        </p>
      </div>
    </div>
  );
}
